"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { AppShell } from "@/components/atsa/AppShell";
import { KeyboardCheck } from "@/components/atsa/KeyboardCheck";
import { ModeSelector } from "@/components/atsa/ModeSelector";
import { Results } from "@/components/atsa/Results";
import { TestChrome } from "@/components/atsa/TestChrome";
import { ATSA_PROFILE_2026, type PracticeMode } from "@/lib/atsa/atsaConfig";
import {
  coversEdges,
  generateRadarWave,
  makeRadarMathProblem,
  RADAR_HEIGHT,
  RADAR_RADIUS,
  RADAR_WIDTH,
  type RadarMathProblem,
  type RadarWave,
} from "@/lib/atsa/radar";
import { createRng, formatTime } from "@/lib/atsa/utils";

type Phase = "options" | "running" | "transition" | "result";
type Part = 1 | 2;
type PartChoice = "1" | "2" | "both";
type ZeroRule = "initially_conflict_free_only" | "all_conflicts_resolved";

type Stats = {
  collisions: number;
  waves: number;
  actualRemovals: number;
  optimalRemovals: number;
  excessRemovals: number;
  unresolvedEdges: number;
  zeroCorrect: number;
  zeroWrong: number;
  mathCorrect: number;
  mathPresented: number;
};

type MathState = {
  problem: RadarMathProblem;
  visible: boolean;
  answered: boolean;
};

type CollisionFlash = { x: number; y: number; until: number };

const INITIAL_STATS: Stats = {
  collisions: 0,
  waves: 0,
  actualRemovals: 0,
  optimalRemovals: 0,
  excessRemovals: 0,
  unresolvedEdges: 0,
  zeroCorrect: 0,
  zeroWrong: 0,
  mathCorrect: 0,
  mathPresented: 0,
};

export default function RadarSimulationPage() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const waveRef = useRef<RadarWave | null>(null);
  const removedRef = useRef<Set<number>>(new Set());
  const collisionPairsRef = useRef<Set<string>>(new Set());
  const zeroPressedRef = useRef(false);
  const removalLockedRef = useRef(false);
  const accumulatorRef = useRef(0);
  const previousFrameRef = useRef<number | null>(null);
  const waveCounterRef = useRef(0);
  const mathVisibleTimerRef = useRef<number | null>(null);
  const mathNextTimerRef = useRef<number | null>(null);
  const collisionFlashesRef = useRef<CollisionFlash[]>([]);
  const sessionSeedRef = useRef(20260613);
  const activePartRef = useRef<Part>(1);
  const mathCounterRef = useRef(0);

  const [mode, setMode] = useState<PracticeMode>("training");
  const [phase, setPhase] = useState<Phase>("options");
  const [partChoice, setPartChoice] = useState<PartChoice>("1");
  const [activePart, setActivePart] = useState<Part>(1);
  const [durationSeconds, setDurationSeconds] = useState(120);
  const [difficulty, setDifficulty] = useState(3);
  const [zeroRule, setZeroRule] = useState<ZeroRule>("all_conflicts_resolved");
  const [remaining, setRemaining] = useState(120);
  const [stats, setStats] = useState<Stats>(INITIAL_STATS);
  const [mathState, setMathState] = useState<MathState | null>(null);
  const [zeroLocked, setZeroLocked] = useState(false);
  const [waveNumber, setWaveNumber] = useState(0);
  const [seedInput, setSeedInput] = useState("20260613");
  const [currentSeed, setCurrentSeed] = useState(20260613);
  const [adaptive, setAdaptive] = useState(false);
  const adaptiveRef = useRef(false);
  const adaptiveDifficultyRef = useRef(3);

  const effectiveDuration = mode === "simulation" ? ATSA_PROFILE_2026.radar.secondsPerPart : durationSeconds;
  const effectivePartChoice: PartChoice = mode === "simulation" ? "both" : partChoice;

  const clearMathTimers = useCallback(() => {
    if (mathVisibleTimerRef.current !== null) window.clearTimeout(mathVisibleTimerRef.current);
    if (mathNextTimerRef.current !== null) window.clearTimeout(mathNextTimerRef.current);
    mathVisibleTimerRef.current = null;
    mathNextTimerRef.current = null;
  }, []);

  const evaluateCurrentWave = useCallback(() => {
    const wave = waveRef.current;
    if (!wave) return;
    const removed = new Set(removedRef.current);
    const resolved = coversEdges(removed, wave.originalEdges);
    const uncovered = wave.originalEdges.filter(([a, b]) => !removed.has(a) && !removed.has(b)).length;
    const excess = resolved ? Math.max(0, removed.size - wave.minimumCoverSize) : Math.max(0, removed.size - wave.minimumCoverSize);
    if (adaptiveRef.current) {
      if (uncovered === 0 && excess === 0) adaptiveDifficultyRef.current = Math.min(5, adaptiveDifficultyRef.current + 1);
      else if (uncovered > 0) adaptiveDifficultyRef.current = Math.max(1, adaptiveDifficultyRef.current - 1);
    }
    setStats((current) => ({
      ...current,
      waves: current.waves + 1,
      actualRemovals: current.actualRemovals + removed.size,
      optimalRemovals: current.optimalRemovals + wave.minimumCoverSize,
      excessRemovals: current.excessRemovals + excess,
      unresolvedEdges: current.unresolvedEdges + uncovered,
    }));
    waveRef.current = null;
  }, []);

  const spawnWave = useCallback(() => {
    evaluateCurrentWave();
    waveCounterRef.current += 1;
    const waveSeed = sessionSeedRef.current + waveCounterRef.current * 7919 + activePartRef.current * 104729;
    const activeDifficulty = adaptiveRef.current ? adaptiveDifficultyRef.current : difficulty;
    const next = generateRadarWave(waveCounterRef.current, activeDifficulty, waveSeed);
    next.startedAt = performance.now();
    waveRef.current = next;
    removedRef.current = new Set();
    collisionPairsRef.current = new Set();
    collisionFlashesRef.current = [];
    zeroPressedRef.current = false;
    removalLockedRef.current = false;
    setZeroLocked(false);
    setWaveNumber(next.id);
  }, [difficulty, evaluateCurrentWave]);

  const scheduleMath = useCallback(() => {
    clearMathTimers();
    if (phase !== "running" || activePart !== 2) return;
    mathCounterRef.current += 1;
    const mathSeed = sessionSeedRef.current + 5_000_003 + activePartRef.current * 65_537 + mathCounterRef.current * 4_099;
    const mathDifficulty = adaptiveRef.current ? adaptiveDifficultyRef.current : difficulty;
    const problem = makeRadarMathProblem(mathDifficulty, mathSeed);
    const intervalMs = createRng(mathSeed ^ 0x5f3759df).int(
      ATSA_PROFILE_2026.radar.mathIntervalMinMs,
      ATSA_PROFILE_2026.radar.mathIntervalMaxMs,
    );
    setMathState({ problem, visible: true, answered: false });
    setStats((current) => ({ ...current, mathPresented: current.mathPresented + 1 }));
    mathVisibleTimerRef.current = window.setTimeout(() => {
      setMathState((current) => current ? { ...current, visible: false } : current);
    }, ATSA_PROFILE_2026.radar.mathVisibleMs);
    mathNextTimerRef.current = window.setTimeout(() => {
      scheduleMath();
    }, intervalMs);
  }, [activePart, clearMathTimers, difficulty, phase]);

  const beginPart = useCallback((part: Part) => {
    activePartRef.current = part;
    setActivePart(part);
    setRemaining(effectiveDuration);
    setMathState(null);
    waveRef.current = null;
    removedRef.current = new Set();
    collisionPairsRef.current = new Set();
    zeroPressedRef.current = false;
    removalLockedRef.current = false;
    previousFrameRef.current = null;
    accumulatorRef.current = 0;
    setZeroLocked(false);
    setPhase("running");
    window.setTimeout(spawnWave, 0);
  }, [effectiveDuration, spawnWave]);

  const begin = useCallback(() => {
    const parsedSeed = Number.parseInt(seedInput, 10);
    const seed = Number.isFinite(parsedSeed) ? parsedSeed >>> 0 : 20260613;
    sessionSeedRef.current = seed;
    setCurrentSeed(seed);
    adaptiveRef.current = adaptive;
    adaptiveDifficultyRef.current = difficulty;
    setStats(INITIAL_STATS);
    waveCounterRef.current = 0;
    mathCounterRef.current = 0;
    collisionFlashesRef.current = [];
    const firstPart: Part = effectivePartChoice === "2" ? 2 : 1;
    beginPart(firstPart);
  }, [adaptive, beginPart, difficulty, effectivePartChoice, seedInput]);

  const finishPart = useCallback(() => {
    evaluateCurrentWave();
    clearMathTimers();
    if (effectivePartChoice === "both" && activePart === 1) {
      setPhase("transition");
      window.setTimeout(() => beginPart(2), 2500);
      return;
    }
    setPhase("result");
  }, [activePart, beginPart, clearMathTimers, effectivePartChoice, evaluateCurrentWave]);

  useEffect(() => {
    if (phase !== "running") return;
    const timer = window.setInterval(() => {
      setRemaining((value) => {
        if (value <= 1) {
          window.clearInterval(timer);
          window.setTimeout(finishPart, 0);
          return 0;
        }
        return value - 1;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [finishPart, phase]);

  useEffect(() => {
    if (phase === "running" && activePart === 2) scheduleMath();
    else clearMathTimers();
    return clearMathTimers;
  }, [activePart, clearMathTimers, phase, scheduleMath]);

  useEffect(() => {
    if (phase !== "running") return;
    let frame = 0;
    const stepSeconds = 1 / 60;
    const draw = (timestamp: number) => {
      const canvas = canvasRef.current;
      const context = canvas?.getContext("2d");
      if (!canvas || !context) return;
      const previous = previousFrameRef.current ?? timestamp;
      const elapsedSeconds = Math.min(0.25, (timestamp - previous) / 1000);
      previousFrameRef.current = timestamp;
      accumulatorRef.current += elapsedSeconds;

      while (accumulatorRef.current >= stepSeconds) {
        const wave = waveRef.current;
        if (wave) {
          for (const target of wave.targets) {
            target.x += target.vx * stepSeconds;
            target.y += target.vy * stepSeconds;
          }
          const removedByCollision = new Set<number>();
          for (let first = 0; first < wave.targets.length; first += 1) {
            for (let second = first + 1; second < wave.targets.length; second += 1) {
              const a = wave.targets[first]!;
              const b = wave.targets[second]!;
              // Physical-contact radius: two targets actually overlap, which
              // registers a collision and triggers the red flash. Distinct by
              // design from the wider 2.1-radius scoring/conflict threshold.
              if (Math.hypot(a.x - b.x, a.y - b.y) <= RADAR_RADIUS * 1.9) {
                const key = [a.id, b.id].sort((x, y) => x - y).join("-");
                if (!collisionPairsRef.current.has(key)) {
                  collisionPairsRef.current.add(key);
                  collisionFlashesRef.current.push({
                    x: (a.x + b.x) / 2,
                    y: (a.y + b.y) / 2,
                    until: timestamp + ATSA_PROFILE_2026.radar.collisionFlashMs,
                  });
                  setStats((current) => ({ ...current, collisions: current.collisions + 1 }));
                }
                removedByCollision.add(a.id);
                removedByCollision.add(b.id);
              }
            }
          }
          wave.targets = wave.targets.filter((target) => {
            if (removedByCollision.has(target.id)) return false;
            return target.x > -80 && target.x < RADAR_WIDTH + 80 && target.y > -80 && target.y < RADAR_HEIGHT + 80;
          });
          if (timestamp - wave.startedAt >= wave.durationMs || wave.targets.length === 0) spawnWave();
        }
        accumulatorRef.current -= stepSeconds;
      }

      context.clearRect(0, 0, RADAR_WIDTH, RADAR_HEIGHT);
      context.fillStyle = "#020403";
      context.fillRect(0, 0, RADAR_WIDTH, RADAR_HEIGHT);
      context.strokeStyle = mode === "training" ? "rgba(40,104,55,.28)" : "rgba(40,104,55,.16)";
      context.lineWidth = 1;
      for (let x = 100; x < RADAR_WIDTH; x += 100) {
        context.beginPath(); context.moveTo(x, 0); context.lineTo(x, RADAR_HEIGHT); context.stroke();
      }
      for (let y = 100; y < RADAR_HEIGHT; y += 100) {
        context.beginPath(); context.moveTo(0, y); context.lineTo(RADAR_WIDTH, y); context.stroke();
      }
      const wave = waveRef.current;
      if (wave) {
        for (const target of wave.targets) {
          context.beginPath();
          context.arc(target.x, target.y, RADAR_RADIUS, 0, Math.PI * 2);
          context.fillStyle = "#f2f2ef";
          context.fill();
          context.strokeStyle = "#c8c8c3";
          context.lineWidth = 1.5;
          context.stroke();
          context.fillStyle = "#b01828";
          context.font = "700 16px Arial, sans-serif";
          context.textAlign = "center";
          context.textBaseline = "middle";
          context.fillText(String(target.id), target.x, target.y + 0.5);
        }
      }

      collisionFlashesRef.current = collisionFlashesRef.current.filter((flash) => flash.until > timestamp);
      for (const flash of collisionFlashesRef.current) {
        const remainingRatio = Math.max(0, (flash.until - timestamp) / ATSA_PROFILE_2026.radar.collisionFlashMs);
        context.beginPath();
        context.arc(flash.x, flash.y, RADAR_RADIUS * (1.8 + (1 - remainingRatio)), 0, Math.PI * 2);
        context.fillStyle = `rgba(210, 36, 43, ${0.35 + remainingRatio * 0.55})`;
        context.fill();
        context.strokeStyle = `rgba(255, 115, 115, ${remainingRatio})`;
        context.lineWidth = 3;
        context.stroke();
      }

      frame = requestAnimationFrame(draw);
    };
    frame = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(frame);
      previousFrameRef.current = null;
    };
  }, [mode, phase, spawnWave]);

  useEffect(() => {
    if (phase !== "running") return;
    const onKey = (event: KeyboardEvent) => {
      if (event.repeat) return;
      const wave = waveRef.current;
      const numpadTarget = /^Numpad[1-9]$/.test(event.code);
      const trainingTarget = numpadTarget || /^[1-9]$/.test(event.key);
      if (mode === "simulation" ? numpadTarget : trainingTarget) {
        event.preventDefault();
        if (!wave || removalLockedRef.current) return;
        const id = Number(event.code.startsWith("Numpad") ? event.code.replace("Numpad", "") : event.key);
        if (!wave.targets.some((target) => target.id === id)) return;
        wave.targets = wave.targets.filter((target) => target.id !== id);
        removedRef.current.add(id);
        return;
      }

      const zeroMatch = event.code === "Numpad0" || (mode === "training" && event.key === "0");
      if (zeroMatch) {
        event.preventDefault();
        if (!wave || removalLockedRef.current) return;
        const correct = zeroRule === "initially_conflict_free_only"
          ? wave.originalEdges.length === 0
          : coversEdges(removedRef.current, wave.originalEdges);
        zeroPressedRef.current = true;
        removalLockedRef.current = true;
        setZeroLocked(true);
        setStats((current) => ({
          ...current,
          zeroCorrect: current.zeroCorrect + (correct ? 1 : 0),
          zeroWrong: current.zeroWrong + (correct ? 0 : 1),
        }));
        return;
      }

      if (activePart === 2 && ["KeyA", "KeyS", "KeyD", "KeyF"].includes(event.code)) {
        event.preventDefault();
        setMathState((current) => {
          if (!current || current.answered) return current;
          const index = ["KeyA", "KeyS", "KeyD", "KeyF"].indexOf(event.code);
          if (index === current.problem.answerIndex) setStats((statsValue) => ({ ...statsValue, mathCorrect: statsValue.mathCorrect + 1 }));
          return { ...current, answered: true };
        });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activePart, mode, phase, zeroRule]);

  useEffect(() => () => clearMathTimers(), [clearMathTimers]);

  const efficiency = stats.actualRemovals > 0
    ? Math.max(0, Math.round((stats.optimalRemovals / stats.actualRemovals) * 100))
    : stats.optimalRemovals === 0 ? 100 : 0;
  const activeSimulation = mode === "simulation" && (phase === "running" || phase === "transition");

  return (
    <AppShell hideChrome={activeSimulation}>
      <TestChrome
        title="Radar Simulation"
        mode={activeSimulation ? "simulation" : "training"}
        status={phase === "running" ? `Part ${activePart} · ${formatTime(remaining)}` : phase === "transition" ? "Part 1 complete" : undefined}
        onRestart={mode === "training" && phase !== "options" ? begin : undefined}
      >
        {phase === "options" && (
          <section className="panel">
            <div className="panel-header">
              <p className="eyebrow">REPORTED SECTION 4</p>
              <h2>Radar Simulation</h2>
              <p>Remove the fewest numbered targets needed to prevent collisions. Part 2 adds short-lived arithmetic without pausing the traffic.</p>
            </div>
            <ModeSelector value={mode} onChange={(next) => {
              setMode(next);
              if (next === "simulation") {
                setPartChoice("both");
                setDurationSeconds(600);
                setDifficulty(3);
              }
            }} />
            {mode === "training" && (
              <div className="options-grid">
                <div className="control-group">
                  <label>Part</label>
                  <div className="segmented">
                    {([['1', 'Part 1'], ['2', 'Part 2'], ['both', 'Both']] as const).map(([value, label]) => <button key={value} className={partChoice === value ? "active" : ""} onClick={() => setPartChoice(value)}>{label}</button>)}
                  </div>
                </div>
                <div className="control-group">
                  <label htmlFor="radar-duration">Seconds per part</label>
                  <select id="radar-duration" value={durationSeconds} onChange={(event) => setDurationSeconds(Number(event.target.value))}><option value={60}>60 seconds</option><option value={120}>2 minutes</option><option value={300}>5 minutes</option><option value={600}>10 minutes</option></select>
                </div>
                <div className="control-group">
                  <span className="profile-label">Difficulty profile</span>
                  <div className="profile-row">
                    {([['Easy', 'Warm-up', 1, false], ['Standard', 'Test-like', 3, true], ['Hard', 'Harder', 5, false]] as const).map(([label, tag, val, std]) => (
                      <button type="button" key={label} className={`profile-chip ${!adaptive && difficulty === val ? "active" : ""}`} onClick={() => { setAdaptive(false); setDifficulty(val); }}>
                        <span className="tag">{tag}</span>{label}{std ? <span className="std-flag">★ Standard</span> : null}
                      </button>
                    ))}
                    <button type="button" className={`profile-chip ${adaptive ? "active" : ""}`} onClick={() => { setAdaptive(true); setDifficulty(3); }}>
                      <span className="tag">Auto</span>Adaptive
                    </button>
                  </div>
                  <label htmlFor="radar-difficulty" style={{ marginTop: 10 }}>Fine tune: {difficulty}</label>
                  <input id="radar-difficulty" type="range" min={1} max={5} value={difficulty} onChange={(event) => { setAdaptive(false); setDifficulty(Number(event.target.value)); }} />
                  {adaptive ? <small className="field-note">Adaptive starts at the current difficulty, then steps up after a clean wave and down after an unresolved one. Replay seed still fixes the wave layout at each level.</small> : null}
                </div>
              </div>
            )}
            <div className="control-group">
              <label htmlFor="radar-seed">Replay seed</label>
              <div className="seed-control-row">
                <input id="radar-seed" inputMode="numeric" value={seedInput} onChange={(event) => setSeedInput(event.target.value.replace(/\D/g, "").slice(0, 10))} />
                <button type="button" className="secondary-button" onClick={() => setSeedInput(String(Date.now() >>> 0))}>New seed</button>
              </div>
              <small className="field-note">Use the same seed to reproduce the same radar waves and math sequence.</small>
            </div>
            <div className="control-group">
              <label htmlFor="zero-rule">Zero-key profile</label>
              <select id="zero-rule" value={zeroRule} onChange={(event) => setZeroRule(event.target.value as ZeroRule)}>
                <option value="all_conflicts_resolved">Press 0 after all conflicts are resolved</option>
                <option value="initially_conflict_free_only">Press 0 only for an initially conflict-free wave</option>
              </select>
              <small className="field-note">Public reports conflict on this rule, so the platform preserves both profiles.</small>
            </div>
            <ol className="instructions">
              <li>All targets in a wave appear together.</li>
              <li>Press Numpad 1–9 to remove a target. Press Numpad 0 according to the selected profile.</li>
              <li>Part 2 math uses A, S, D, and F. The problem disappears after about three seconds, but the answer remains valid until the next problem.</li>
              <li>Every active target uses identical styling. A brief red flash appears only after an actual collision; it never predicts a threat.</li>
            </ol>
            <KeyboardCheck />
            <button className="primary-button" onClick={begin}>Start {mode === "simulation" ? "Simulation" : "Training"}</button>
          </section>
        )}

        {phase === "running" && (
          <section className={activeSimulation ? "fidelity-radar-layout" : "radar-wrap"}>
            {mode === "training" && (
              <div className="radar-scorebar">
                <div className="radar-stat">Wave<strong>{waveNumber}</strong></div>
                <div className="radar-stat">Collisions<strong>{stats.collisions}</strong></div>
                <div className="radar-stat">Removals<strong>{stats.actualRemovals + removedRef.current.size}</strong></div>
                <div className="radar-stat">Zero correct<strong>{stats.zeroCorrect}</strong></div>
                <div className="radar-stat">Math<strong>{stats.mathCorrect}/{stats.mathPresented}</strong></div>
              </div>
            )}
            <div className="radar-test-grid">
              <div className="radar-screen fidelity-radar-screen">
                <canvas ref={canvasRef} className="radar-canvas" width={RADAR_WIDTH} height={RADAR_HEIGHT} />
                {mode === "training" && zeroLocked && <div className="radar-lock-label">ZERO LOCKED</div>}
              </div>
              {activePart === 2 && (
                <div className={`math-card fidelity-math-card ${mathState?.visible ? "visible" : "hidden-problem"}`}>
                  {mathState?.visible ? (
                    <>
                      <div className="math-question">{mathState.problem.prompt}</div>
                      <div className="math-options">
                        {mathState.problem.choices.map((choice, index) => <div className="math-option" key={`${choice}-${index}`}>{["A", "S", "D", "F"][index]}) {choice}</div>)}
                      </div>
                    </>
                  ) : (
                    <div className="math-placeholder">Continue monitoring. The previous answer remains active until the next problem.</div>
                  )}
                  {mode === "training" && mathState?.answered && <div className="answer-recorded">Answer recorded</div>}
                </div>
              )}
            </div>
          </section>
        )}

        {phase === "transition" && (
          <section className={activeSimulation ? "fidelity-stage" : "stage"}><div className="stage-content"><h2>Part 1 complete</h2><p>Part 2 begins automatically.</p></div></section>
        )}

        {phase === "result" && (
          <Results
            section="Radar Simulation"
            record={{ sectionSlug: "radar-simulation", score: Math.max(0, Math.round(efficiency - stats.collisions * 15)), scoreLabel: "Safety-efficiency", detail: { collisions: stats.collisions, efficiency } }}
            title="Radar Simulation Complete"
            metrics={[
              { label: "Collisions", value: stats.collisions },
              { label: "Actual removals", value: stats.actualRemovals },
              { label: "Minimum removals", value: stats.optimalRemovals },
              { label: "Excess removals", value: stats.excessRemovals },
              { label: "Removal efficiency", value: `${efficiency}%` },
              { label: "Unresolved edges", value: stats.unresolvedEdges },
              { label: "Zero calls", value: `${stats.zeroCorrect}/${stats.zeroCorrect + stats.zeroWrong}` },
              { label: "Math", value: `${stats.mathCorrect}/${stats.mathPresented}` },
              { label: "Replay seed", value: currentSeed },
            ]}
            onAgain={begin}
            onOptions={() => setPhase("options")}
          />
        )}
      </TestChrome>
    </AppShell>
  );
}
